import { LitElement, html, TemplateResult } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import {
  elementClassListAdd,
  elementClassListRemove,
  isElementOverflowed,
  OverflowDirection,
} from '../../../../utils/htmlElement.utils';
import { WEB_COMPONENT_GZ_COPY_TO_CLIPBOARD } from '../../../../webcomponents.constants';
import { GzCopyToClipboardWebComponent } from '../../../../ui/gzCopyToClipboard';
import { GzTooltip } from '../../../../ui/gzTooltip';
import { isEmpty } from '@nirvana/shared/basic-types/object';

export class GridCellDefaultRendererWebComponent extends LitElement {
  @property({ type: String, reflect: true })
  label: string;

  /**
   * This is programmatically set to TRUE when the grid row containing this renderer is selected.
   */
  @property({ type: Boolean, reflect: true })
  selected = false;

  /**
   * If true and the grid row containing this renderer is not selected, a copyToClipboard button will be displayed on mouseenter.
   * If the grid row is selected, the button will be displayed by default.
   */
  @property({ type: Boolean, reflect: true })
  canCopyContent = false;

  /**
   * This message will be displayed in a tooltip when the `copy to clipboard` button is clicked
   */
  @property({ type: String, reflect: true })
  copyContentMessage: string;

  /**
   * If set, provides a custom message to be copied to clipboard instead of the cell inner text
   */
  @property({ type: String, reflect: true })
  clipboardMessage: string;

  @query(WEB_COMPONENT_GZ_COPY_TO_CLIPBOARD)
  private copyContentBtn!: GzCopyToClipboardWebComponent;

  @query('.cell-label')
  private cellLabel!: HTMLElement;

  static get styles() {
    return styles;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    super.disconnectedCallback();
  }

  render() {
    return html`
      <span
        class="cell-label"
        @mouseenter="${this.handleLabelMouseEnter}"
        @mousemove="${GzTooltip.align}"
        @mouseleave="${GzTooltip.hide}"
        >${this.label}</span
      >
      <div class="copy-content-pivot ${this.selected ? 'visible' : ''}">${this.renderCopyToClipboardButton()}</div>
    `;
  }

  private renderCopyToClipboardButton(): TemplateResult | undefined {
    if (!this.canCopyContent || isEmpty(this.label)) {
      return;
    }
    return html`<gz-copy-to-clipboard
      .confirmationMessage="${this.copyContentMessage}"
      .clipboardMessage="${this.clipboardMessage}"
      class="copy-content"
    >
      <span class="text-content">${this.label}</span>
    </gz-copy-to-clipboard>`;
  }

  private handleMouseEnter() {
    if (this.canCopyContent && this.copyContentBtn && !this.selected) {
      elementClassListAdd(this.copyContentBtn, 'visible');
    }
  }

  private handleMouseLeave() {
    if (this.canCopyContent && this.copyContentBtn && !this.selected) {
      elementClassListRemove(this.copyContentBtn, 'visible');
    }
  }

  private handleLabelMouseEnter(event: MouseEvent) {
    if (!this.label || !isElementOverflowed(this.cellLabel, OverflowDirection.HORIZONTAL)) {
      return;
    }
    GzTooltip.show(this.label, event);
  }
}
